import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormControlLabel,
  Switch,
  Drawer,
  Tabs,
  Tab
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Refresh, Edit, Delete, MoreVert as MoreVertIcon, Add } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import Toast from '../common/Toast';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateGridActionIconSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerHeaderSx,
  harmonateTabsSx,
  harmonateSwitchSx,
} from '../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import { FC } from '../../constants/functionCodes';

const mergeDocumentTypesById = (lists) => {
  const map = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const dt of list) {
      if (dt && dt.id != null) map.set(dt.id, dt);
    }
  }
  return Array.from(map.values());
};

const normalizeTenantDocumentTypes = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item?.documentTypeId)
    .map((item) => ({
      id: item.documentTypeId,
      name: item.documentTypeName || item.documentTypeId
    }));
};

const ReferenceTemplates = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.REFERENCE_TEMPLATES);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    templateName: '',
    description: '',
    category: 'REF',
    documentTypeId: '',
    glSystemId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  /** Merged REF + GL document types for grid name resolution */
  const [documentTypesLookup, setDocumentTypesLookup] = useState([]);
  /** Fast lookup to infer category from document type id */
  const [documentTypeCategoryMap, setDocumentTypeCategoryMap] = useState({});
  /** Document types for the modal dropdown (current category only) */
  const [categoryDocumentTypes, setCategoryDocumentTypes] = useState([]);
  const [loadingCategoryDocTypes, setLoadingCategoryDocTypes] = useState(false);
  const [glSystems, setGlSystems] = useState([]);
  const [loadingGlSystems, setLoadingGlSystems] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedFields, setExpandedFields] = useState({});
  const [editMappingDialogOpen, setEditMappingDialogOpen] = useState(false);
  const [currentMapping, setCurrentMapping] = useState({
    sourceField: '',
    dataType: 'TEXT',
    isActive: true,
    targetColumn: ''
  });
  const [editingField, setEditingField] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  const [deleteField, setDeleteField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('grid');
  const dataTypeOptions = ['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'DATETIME', 'CURRENCY'];
  const { getToken } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [toastState, setToastState] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  const hideToast = () => {
    setToastState(prev => ({ ...prev, open: false }));
  };

  const showSuccess = (message) => showToast(message, 'success');
  const showError = (message) => showToast(message, 'error');

  const resolveTemplateCategory = (template) => {
    const explicitCategory = template?.category || template?.documentTypeCategory || template?.docTypeCategory;
    if (explicitCategory === 'GL' || explicitCategory === 'REF') return explicitCategory;
    const mapped = template?.documentTypeId ? documentTypeCategoryMap[template.documentTypeId] : null;
    return mapped === 'GL' ? 'GL' : 'REF';
  };

  // Get document type name by ID
  const getDocumentTypeName = (id) => {
    if (!id) return '';
    const docType = documentTypesLookup.find(dt => dt.id === id);
    return docType ? docType.name : id;
  };

  const getGlSystemValue = (system) => system?.systemId || system?.system_id || system?.id || '';

  const resolveGlSystemValue = (id) => {
    if (!id) return null;
    const system = glSystems.find(s => getGlSystemValue(s) === id || s.id === id);
    return system ? getGlSystemValue(system) : id;
  };

  // Get GL system name by systemId
  const getGlSystemName = (id) => {
    if (!id) return '';
    const system = glSystems.find(s => getGlSystemValue(s) === id || s.id === id);
    return system ? system.name : id;
  };

  // Define columns for the DataGrid
  const columns = [
    { 
      field: 'templateName', 
      headerName: 'Name', 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 2,
      minWidth: 300,
    },
    { 
      field: 'documentTypeId', 
      headerName: 'Document Type', 
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => getDocumentTypeName(params.row.documentTypeId)
    },
    { 
      field: 'glSystemId', 
      headerName: 'GL System', 
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => getGlSystemName(params.row.glSystemId)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTemplate(params.row);
            setMenuAnchorEl(e.currentTarget);
          }}
          sx={harmonateGridActionIconSx}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Mapping columns for grid view
  const mappingColumns = [
    { field: 'sourceField', headerName: 'Source Field', flex: 1, minWidth: 150 },
    { field: 'targetColumn', headerName: 'Target Column', flex: 1, minWidth: 150 },
    { field: 'dataType', headerName: 'Data Type', flex: 1, minWidth: 120 },
    {
      field: 'isActive',
      headerName: 'Active',
      type: 'boolean',
      width: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch checked={params.value} disabled size="small" />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditMapping(params.row.sourceField, {
                  dataType: params.row.dataType,
                  targetColumn: params.row.targetColumn,
                  isActive: params.row.isActive
                });
              }}
              disabled={submitting}
              sx={harmonateGridActionIconSx}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteMappingClick(params.row.sourceField);
              }}
              disabled={submitting}
              sx={harmonateGridActionIconSx}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Mapping rows for grid view
  const mappingRows = useMemo(() => 
    Object.entries(selectedTemplate?.mapping || {}).map(([sourceField, config]) => ({
      id: sourceField,
      sourceField,
      targetColumn: config.targetColumn || sourceField,
      dataType: config.dataType || 'TEXT',
      isActive: config.isActive !== false
    })), 
  [selectedTemplate]
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category') {
      setFormData(prev => ({
        ...prev,
        category: value,
        documentTypeId: ''
      }));
      if (formErrors.documentTypeId || formErrors.category) {
        setFormErrors(prev => ({
          ...prev,
          documentTypeId: '',
          category: ''
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if ((isEditMode && !canEdit) || (!isEditMode && !canAdd)) return;
    
    // Basic validation
    const errors = {};
    if (!formData.templateName) errors.templateName = 'Template name is required';
    if (!formData.category) errors.category = 'Category is required';
    // Document type and GL System are optional.

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const url = isEditMode 
        ? `${process.env.REACT_APP_BACKEND_URL || ''}conductor/ref-templates/${formData.templateId}`
        : `${process.env.REACT_APP_BACKEND_URL || ''}conductor/ref-templates`;
        
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateName: formData.templateName,
          description: formData.description,
          version: formData.version,
          ...(formData.mapping ? { mapping: formData.mapping } : {}),
          category: formData.category,
          documentTypeId: formData.documentTypeId || null,
          glSystemId: resolveGlSystemValue(formData.glSystemId)
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create template: ${response.status}`);
      }

      const updatedTemplate = await response.json();
      setTemplates(prev => 
        isEditMode 
          ? prev.map(t => t.templateId === updatedTemplate.templateId ? updatedTemplate : t)
          : [...prev, updatedTemplate]
      );
      setOpenCreateDialog(false);
      showSuccess(`Template ${isEditMode ? 'updated' : 'created'} successfully`);
      // Reset form
      setFormData({
        templateName: '',
        description: '',
        category: 'REF',
        documentTypeId: '',
        glSystemId: ''
      });
    } catch (error) {
      console.error('Error creating template:', error);
      showError(error.message || 'Failed to create template');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch templates from API
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingGlSystems(true);
      
      // Store the current scroll position
      const scrollPosition = window.scrollY;
      
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!tenantId) {
        throw new Error('Tenant ID not found in local storage');
      }
      
      // Fetch document types for both categories (grid lookup)
      const baseUrl = `${process.env.REACT_APP_BACKEND_URL || ''}`;
      const authHeaders = { Authorization: `Bearer ${token}` };
      const [refRes, glRes] = await Promise.all([
        fetch(`${baseUrl}conductor/document-type-tenant-configs/tenant/${tenantId}?category=REF`, { headers: authHeaders }),
        fetch(`${baseUrl}conductor/document-type-tenant-configs/tenant/${tenantId}?category=GL`, { headers: authHeaders })
      ]);
      const refData = refRes.ok ? normalizeTenantDocumentTypes(await refRes.json()) : [];
      const glData = glRes.ok ? normalizeTenantDocumentTypes(await glRes.json()) : [];
      if (!refRes.ok && !glRes.ok) {
        throw new Error('Failed to fetch document types');
      }
      setDocumentTypesLookup(mergeDocumentTypesById([refData, glData]));
      const nextCategoryMap = {};
      refData.forEach((dt) => { nextCategoryMap[dt.id] = 'REF'; });
      glData.forEach((dt) => { nextCategoryMap[dt.id] = 'GL'; });
      setDocumentTypeCategoryMap(nextCategoryMap);
      
      // Fetch GL systems (tenant-scoped)
      const glSystemsResponse = await fetch(
        `${baseUrl}conductor/tenant-systems/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
        { headers: authHeaders }
      );
      if (!glSystemsResponse.ok) throw new Error('Failed to fetch GL systems');
      const glSystemsData = await glSystemsResponse.json();
      setGlSystems(Array.isArray(glSystemsData) ? glSystemsData : []);
      setLoadingGlSystems(false);
      
      // Fetch templates
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}conductor/ref-templates`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.status}`);
      }

      const data = await response.json();
      setTemplates(data);
      
      // Restore scroll position after state update
      window.requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
      
      return data; // Return the data so it can be used by other functions
    } catch (error) {
      console.error('Error fetching reference templates:', error);
      setError(error.message || 'Failed to load templates');
      showError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
      setLoadingGlSystems(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Load document types for the modal from the endpoint matching selected category (REF / GL)
  useEffect(() => {
    if (!openCreateDialog) return undefined;

    let cancelled = false;
    const category = formData.category === 'GL' ? 'GL' : 'REF';

    const run = async () => {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) return;
      if (!tenantId) {
        showError('Tenant ID not found in local storage');
        return;
      }
      setLoadingCategoryDocTypes(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ''}conductor/document-type-tenant-configs/tenant/${tenantId}?category=${category}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch document types');
        const data = normalizeTenantDocumentTypes(await res.json());
        if (cancelled) return;
        setCategoryDocumentTypes(Array.isArray(data) ? data : []);
        setDocumentTypesLookup((prev) => mergeDocumentTypesById([prev, data]));
        setDocumentTypeCategoryMap((prev) => {
          const next = { ...prev };
          data.forEach((dt) => { next[dt.id] = category; });
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching document types by category:', err);
          showError(err.message || 'Failed to fetch document types');
          setCategoryDocumentTypes([]);
        }
      } finally {
        if (!cancelled) setLoadingCategoryDocTypes(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [openCreateDialog, formData.category]);

  // Handle row click to show mapping details
  const handleRowClick = (params) => {
    setSelectedTemplate(params.row);
    setActiveTab('grid');
    setDrawerOpen(true);
  };

  // Toggle field expansion in mapping details
  const handleExpandField = (field) => {
    setExpandedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Handle opening the edit mapping dialog
  const handleOpenEditMapping = (field = null, mappingData = null) => {
    if (field && mappingData) {
      setEditingField(field);
      setCurrentMapping({
        sourceField: field,
        dataType: mappingData.dataType || 'TEXT',
        isActive: mappingData.isActive !== false, // Default to true if not specified
        targetColumn: mappingData.targetColumn || ''
      });
    } else {
      setEditingField(null);
      setCurrentMapping({
        sourceField: '',
        dataType: 'TEXT',
        isActive: true,
        targetColumn: ''
      });
    }
    setEditMappingDialogOpen(true);
  };

  // Handle saving the mapping
  const handleSaveMapping = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token || !selectedTemplate) return;

      const updatedMapping = { ...selectedTemplate.mapping };
      
      // Remove old field if editing
      if (editingField && editingField !== currentMapping.sourceField) {
        delete updatedMapping[editingField];
      }
      
      // Add/update the field
      updatedMapping[currentMapping.sourceField] = {
        dataType: currentMapping.dataType,
        isActive: currentMapping.isActive,
        targetColumn: currentMapping.targetColumn || currentMapping.sourceField
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}conductor/ref-templates/${selectedTemplate.templateId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...selectedTemplate,
            mapping: updatedMapping
          })
        }
      );

      if (!response.ok) throw new Error('Failed to update mapping');
      
      // Refresh the template data and update the selected template
      const updatedTemplates = await fetchTemplates();
      const updatedTemplate = updatedTemplates.find(t => t.templateId === selectedTemplate.templateId);
      if (updatedTemplate) {
        setSelectedTemplate(updatedTemplate);
      }
      setEditMappingDialogOpen(false);
      showSuccess('Mapping saved successfully');
    } catch (error) {
      console.error('Error saving mapping:', error);
      showError('Failed to save mapping');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle template delete confirmation
  const handleDeleteClick = () => {
    if (!canDelete || !selectedTemplate) return;
    setDeleteTemplate(selectedTemplate);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Confirm template delete
  const handleConfirmDelete = async () => {
    if (!canDelete || !deleteTemplate) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}conductor/ref-templates/${deleteTemplate.templateId}`,
        {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete template: ${response.status}`);
      }

      setDeleteDialogOpen(false);
      setDeleteTemplate(null);
      await fetchTemplates();
      showSuccess('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      showError(error.message || 'Failed to delete template');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle field delete confirmation
  const handleDeleteMappingClick = (field) => {
    setDeleteField(field);
    setDeleteFieldDialogOpen(true);
  };

  // Confirm field delete
  const handleConfirmDeleteField = async () => {
    if (!deleteField || !selectedTemplate) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const updatedMapping = { ...selectedTemplate.mapping };
      delete updatedMapping[deleteField];

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}conductor/ref-templates/${selectedTemplate.templateId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...selectedTemplate,
            mapping: updatedMapping
          })
        }
      );

      if (!response.ok) throw new Error('Failed to delete mapping');
      
      // Refresh the template data and update the selected template
      const updatedTemplates = await fetchTemplates();
      const updatedTemplate = updatedTemplates.find(t => t.templateId === selectedTemplate.templateId);
      if (updatedTemplate) {
        setSelectedTemplate(updatedTemplate);
      }
      setDeleteFieldDialogOpen(false);
      setDeleteField(null);
      showSuccess('Mapping deleted successfully');
    } catch (error) {
      console.error('Error deleting mapping:', error);
      showError('Failed to delete mapping');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle menu actions
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleEdit = () => {
    if (!canEdit || !selectedTemplate) return;
    const resolvedCategory = resolveTemplateCategory(selectedTemplate);
    setFormData({
      templateId: selectedTemplate.templateId,
      templateName: selectedTemplate.templateName,
      description: selectedTemplate.description || '',
      category: resolvedCategory,
      documentTypeId: selectedTemplate.documentTypeId || '',
      glSystemId: resolveGlSystemValue(selectedTemplate.glSystemId || ''),
      mapping: selectedTemplate.mapping
    });
    setIsEditMode(true);
    setOpenCreateDialog(true);
    handleMenuClose();
  };

  if (loading && templates.length === 0) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Reference Templates
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canAdd ? 'Add Template' : 'No add permission'}>
            <span>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  if (!canAdd) return;
                  setSelectedTemplate(null);
                  setIsEditMode(false);
                  setOpenCreateDialog(true);
                }}
                disabled={submitting || !canAdd}
                sx={harmonateProceedButtonSx}
              >
                Add Template
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={fetchTemplates}
                disabled={loading || submitting}
                sx={harmonatePlainIconButtonSx}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          mb: 3,
        }}
      >
        <DataGridPro
          columnResizeMode="onEnd"
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          resizable
          rows={templates}
          columns={columns}
          getRowId={(row) => row.templateId}
          loading={loading}
          onRowClick={handleRowClick}
          disableSelectionOnClick
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
          components={{
            NoRowsOverlay: () => (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No templates found
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchTemplates}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            ),
          }}
        />
      </Paper>

      {/* Mapping Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '40%' },
            minWidth: 400,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: ht.drawerBg,
          },
        }}
      >
        <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
              {selectedTemplate?.templateName || 'Template Mapping'}
            </Typography>
            <Typography variant="body2" sx={{ color: ht.secondaryTab }}>
              Reference Template
            </Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close drawer" sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          {selectedTemplate?.mapping && Object.keys(selectedTemplate.mapping).length > 0 ? (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenEditMapping()}
                  disabled={submitting || !canAdd}
                  sx={harmonateProceedButtonSx}
                >
                  Add Field
                </Button>
              </Box>

              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ ...harmonateTabsSx, mb: 2 }}
              >
                <Tab label="Grid View" value="grid" />
                <Tab label="Raw JSON" value="json" />
              </Tabs>

              <Box sx={{
                p: 2,
                bgcolor: ht.white,
                borderRadius: ht.cardRadius,
                border: `1px solid ${ht.line}`,
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto',
              }}>
                {activeTab === 'grid' ? (
                  <DataGridPro
                    columnResizeMode="onEnd"
                    pagination
                    rowsPerPageOptions={[10, 25, 50]}
                    pageSize={10}
                    autoPageSize={false}
                    resizable
                    rows={mappingRows}
                    columns={mappingColumns}
                    getRowId={(row) => row.id}
                    autoHeight
                    disableRowSelectionOnClick
                    hideFooter
                    sx={{ ...harmonateDataGridSx, border: 'none' }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                          <Typography sx={{ color: ht.text }}>No mappings defined</Typography>
                        </Box>
                      )
                    }}
                  />
                ) : (
                  <Box sx={{ p: 2, bgcolor: ht.listBg, borderRadius: ht.fieldRadius }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', color: ht.text }}>
                      {JSON.stringify(selectedTemplate.mapping, null, 2)}
                    </pre>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60vh',
              textAlign: 'center',
              color: ht.text,
              border: `1px dashed ${ht.line}`,
              borderRadius: ht.cardRadius,
              p: 4,
              bgcolor: ht.white,
            }}>
              <Box sx={{ mb: 2, color: ht.fadedText }}>
                <AddIcon fontSize="large" />
              </Box>
              <Typography variant="h6" gutterBottom sx={{ color: ht.text }}>No Mapping Data</Typography>
              <Typography variant="body2" sx={{ mb: 3, maxWidth: '80%', color: ht.text }}>
                This template doesn't have any mapping fields yet. Click the button below to add your first field.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenEditMapping()}
                disabled={submitting || !canAdd}
                size="large"
                sx={{ ...harmonateProceedButtonSx, mt: 1 }}
              >
                Add First Field
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
      >
        <MenuItem onClick={handleEdit} disabled={!canEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} disabled={!canDelete} sx={{ color: ht.red }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Template Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete the template "{deleteTemplate?.templateName}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={submitting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={submitting}
            sx={harmonateAbortButtonSx}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Field Confirmation Dialog */}
      <Dialog
        open={deleteFieldDialogOpen}
        onClose={() => setDeleteFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete the mapping for "{deleteField}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteFieldDialogOpen(false)}
            disabled={submitting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteField}
            variant="contained"
            disabled={submitting}
            sx={harmonateAbortButtonSx}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => {
          setOpenCreateDialog(false);
          setIsEditMode(false);
          setFormData({
            templateName: '',
            description: '',
            version: 1,
            category: 'REF',
            documentTypeId: '',
            glSystemId: ''
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{isEditMode ? 'Edit Template' : 'Create New Template'}</DialogTitle>
        <form onSubmit={handleCreateTemplate}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              autoFocus
              margin="dense"
              name="templateName"
              label="Template Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.templateName}
              onChange={handleInputChange}
              error={!!formErrors.templateName}
              helperText={formErrors.templateName}
              disabled={submitting}
              InputLabelProps={harmonateInputLabelProps}
            />
            <TextField
              margin="dense"
              name="description"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.description}
              onChange={handleInputChange}
              disabled={submitting}
              InputLabelProps={harmonateInputLabelProps}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth error={!!formErrors.category}>
                <InputLabel sx={harmonateInputLabelProps.sx}>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  label="Category"
                  disabled={submitting}
                  MenuProps={harmonateSelectMenuProps}
                >
                  <MenuItem value="REF">REF</MenuItem>
                  <MenuItem value="GL">GL</MenuItem>
                </Select>
                {formErrors.category && (
                  <FormHelperText>{formErrors.category}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth error={!!formErrors.documentTypeId}>
                <InputLabel sx={harmonateInputLabelProps.sx}>Document Type (optional)</InputLabel>
                <Select
                  name="documentTypeId"
                  value={formData.documentTypeId}
                  onChange={handleInputChange}
                  label="Document Type (optional)"
                  disabled={loadingCategoryDocTypes || submitting}
                  MenuProps={harmonateSelectMenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {loadingCategoryDocTypes ? (
                    <MenuItem disabled>Loading document types...</MenuItem>
                  ) : (
                    categoryDocumentTypes.map((docType) => (
                      <MenuItem key={docType.id} value={docType.id}>
                        {docType.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.documentTypeId && (
                  <FormHelperText>{formErrors.documentTypeId}</FormHelperText>
                )}
              </FormControl>
            </Box>
            <FormControl fullWidth error={!!formErrors.glSystemId}>
              <InputLabel sx={harmonateInputLabelProps.sx}>GL System (optional)</InputLabel>
              <Select
                name="glSystemId"
                value={formData.glSystemId}
                onChange={handleInputChange}
                label="GL System (optional)"
                disabled={loadingGlSystems || submitting}
                MenuProps={harmonateSelectMenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {loadingGlSystems ? (
                  <MenuItem disabled>Loading GL systems...</MenuItem>
                ) : (
                  glSystems.map((system) => (
                    <MenuItem key={system.id} value={getGlSystemValue(system)}>
                      {system.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.glSystemId && (
                <FormHelperText>{formErrors.glSystemId}</FormHelperText>
              )}
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => {
              setOpenCreateDialog(false);
              setIsEditMode(false);
              setFormData({
                templateName: '',
                description: '',
                version: 1,
                category: 'REF',
                documentTypeId: '',
                glSystemId: ''
              });
            }} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Mapping Dialog */}
      <Dialog
        open={editMappingDialogOpen}
        onClose={() => setEditMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{editingField ? 'Edit Mapping' : 'Add New Mapping'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            label="Source Field"
            value={currentMapping.sourceField}
            onChange={(e) => setCurrentMapping({...currentMapping, sourceField: e.target.value})}
            fullWidth
            required
            disabled={submitting || Boolean(editingField)}
            size="small"
            InputLabelProps={harmonateInputLabelProps}
          />

          <FormControl fullWidth size="small">
            <InputLabel sx={harmonateInputLabelProps.sx}>Data Type</InputLabel>
            <Select
              value={currentMapping.dataType}
              label="Data Type"
              onChange={(e) => setCurrentMapping({...currentMapping, dataType: e.target.value})}
              disabled={submitting}
              MenuProps={harmonateSelectMenuProps}
            >
              {dataTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Target Column"
            value={currentMapping.targetColumn}
            onChange={(e) => setCurrentMapping({...currentMapping, targetColumn: e.target.value})}
            fullWidth
            helperText="Leave empty to use source field name"
            disabled={submitting}
            size="small"
            InputLabelProps={harmonateInputLabelProps}
          />

          <FormControlLabel
            control={
              <Switch
                checked={currentMapping.isActive}
                onChange={(e) => setCurrentMapping({...currentMapping, isActive: e.target.checked})}
                disabled={submitting}
                sx={harmonateSwitchSx}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditMappingDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveMapping}
            variant="contained"
            disabled={!currentMapping.sourceField || submitting}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
      />
    </Box>
  );
};

export default ReferenceTemplates;